import React, { useState, useEffect, useContext } from 'react';
import { Modal, ModalHeader, ModalBody, Button, Form, FormGroup, Label, Input } from 'reactstrap';
import { AppContext } from '../../application/provider';
import Loader from '../Loader/Loader';
import AdministracionPlanillaService from '../../axios_services/administracion_planilla.service';
import SueldoEmpleadoService from '../../axios_services/sueldoempleado.service';

const ALLOWED_PERFILES = [1, 5, 9, 11, 21, 22, 26];

const EMPTY_FORM = {
    usuario_id: '',
    sueldo_perfil: 0,
    bono_anual: 0,
    bono_gestion: 0,
    vacaciones_truncas: 0,
    descuento: 0,
    total_aporte: 0,
    retencion: 0,
    importe_neto: 0,
    total_pagar_planilla: 0,
};

const calcTotals = (f, pct = 0.1137) => {
    const total_pagar = (parseFloat(f.sueldo_perfil) || 0)
        + (parseFloat(f.bono_anual) || 0)
        + (parseFloat(f.bono_gestion) || 0)
        + (parseFloat(f.vacaciones_truncas) || 0)
        - (parseFloat(f.descuento) || 0);
    const total_aporte = parseFloat((total_pagar * pct).toFixed(2));
    const importe_neto = parseFloat((total_pagar - total_aporte - (parseFloat(f.retencion) || 0)).toFixed(2));
    return { total_pagar_planilla: parseFloat(total_pagar.toFixed(2)), total_aporte, importe_neto };
};

const ModalAgregarAdministracionCargoPago = ({ isOpen, onClose, onSave, editItem }) => {
    const [state] = useContext(AppContext);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [usuarios, setUsuarios] = useState([]);
    const [sueldos, setSueldos] = useState([]);
    const [porcentaje, setPorcentaje] = useState(0.1137);

    const getPerfilName = (id) => {
        const p = state?.catalogos?.perfiles?.find(p => p.perfil_id === Number(id));
        return p ? p.descripcion : '';
    };

    useEffect(() => {
        const load = async () => {
            try {
                const [usr, sal] = await Promise.all([
                    AdministracionPlanillaService.getUsuariosParaPlanilla().catch(() => []),
                    SueldoEmpleadoService.getSueldosPerfil().catch(() => []),
                ]);
                const arrUsr = Array.isArray(usr) ? usr : (usr?.data || []);
                const arrSal = Array.isArray(sal) ? sal : (sal?.data || []);
                setUsuarios(arrUsr.filter(u => u && ALLOWED_PERFILES.includes(Number(u.perfil_id)) && Number(u.estado) === 1));
                setSueldos(arrSal);
            } catch (e) { console.error(e); }
        };
        if (isOpen) load();
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            if (editItem) {
                const perfil = Number(editItem.perfil_id);
                const pct = perfil === 1 ? 0.1292 : perfil === 9 ? 0.13 : 0.1137;
                setPorcentaje(pct);
                setFormData({
                    usuario_id: editItem.usuario_id || '',
                    sueldo_perfil: editItem.sueldo_perfil || 0,
                    bono_anual: editItem.bono_anual || 0,
                    bono_gestion: editItem.bono_gestion || 0,
                    vacaciones_truncas: editItem.vacaciones_truncas || 0,
                    descuento: editItem.descuento || 0,
                    total_aporte: editItem.total_aporte || 0,
                    retencion: editItem.retencion || 0,
                    importe_neto: editItem.importe_neto || 0,
                    total_pagar_planilla: editItem.total_pagar_planilla || 0,
                });
            } else {
                setFormData(EMPTY_FORM);
                setPorcentaje(0.1137);
            }
        }
    }, [isOpen, editItem]);

    const handleUserChange = (e) => {
        const usuario_id = e.target.value;
        const u = usuarios.find(u => String(u.usuario_id) === String(usuario_id));
        if (!u) { setFormData(prev => ({ ...prev, usuario_id })); return; }
        const perfil = Number(u.perfil_id);
        const pct = perfil === 1 ? 0.1292 : perfil === 9 ? 0.13 : 0.1137;
        setPorcentaje(pct);
        const sueldoObj = sueldos.find(s => Number(s.perfil_id) === perfil && Number(s.estado) === 1);
        const sueldo_perfil = sueldoObj ? parseFloat(sueldoObj.monto_sueldo) : 0;
        const base = { ...EMPTY_FORM, usuario_id, sueldo_perfil };
        const totals = calcTotals(base, pct);
        setFormData({ ...base, ...totals });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [name]: parseFloat(value) || 0 };
            const totals = calcTotals(updated, porcentaje);
            return { ...updated, ...totals };
        });
    };

    const handleSave = async () => {
        if (!formData.usuario_id) {
            alert('Debe seleccionar un colaborador.');
            return;
        }
        setIsLoading(true);
        try {
            await onSave(formData);
        } catch (err) {
            alert('Error al guardar: ' + (err.response?.data?.error || err.message));
        } finally {
            setIsLoading(false);
        }
    };

    const selectedUser = usuarios.find(u => String(u.usuario_id) === String(formData.usuario_id));
    const totals = calcTotals(formData, porcentaje);

    return (
        <Modal isOpen={isOpen} className="custom-modal" backdrop="static" size="md" centered>
            <ModalHeader className="modal-header">
                <div className="titulo">
                    <h1>{editItem ? 'EDITAR REGISTRO' : 'NUEVO REGISTRO'} - PLANILLA ADMINISTRACIÓN</h1>
                    <button className="close-btn" onClick={onClose}>X</button>
                </div>
            </ModalHeader>

            <div className="modal-body-footer">
                <ModalBody className="modal-body p-4">
                    {isLoading && <Loader />}
                    <p className="text-muted mb-4 px-4" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                        Complete la información del colaborador para el periodo seleccionado.
                    </p>

                    <Form className="px-4">

                        <FormGroup className="mb-3">
                            <Label for="usuario_id">Colaborador <span className="text-danger">*</span></Label>
                            <Input
                                type="select"
                                name="usuario_id"
                                id="usuario_id"
                                value={formData.usuario_id}
                                onChange={handleUserChange}
                                className="form-control"
                                disabled={!!editItem}
                            >
                                <option value="">Seleccionar colaborador...</option>
                                {usuarios.map(u => (
                                    <option key={u.usuario_id} value={u.usuario_id}>
                                        {`${u.apellidos} ${u.nombre || u.nombres || ''}`.toUpperCase()} — {getPerfilName(u.perfil_id)}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>

                        {selectedUser && (
                            <>
                                <FormGroup className="mb-3">
                                    <Label>Cargo / Perfil</Label>
                                    <Input type="text" value={getPerfilName(selectedUser.perfil_id)} disabled className="form-control" />
                                </FormGroup>

                                <FormGroup className="mb-3">
                                    <Label>DNI</Label>
                                    <Input type="text" value={selectedUser.dni || '—'} disabled className="form-control" />
                                </FormGroup>

                                <FormGroup className="mb-3">
                                    <Label>N° Cuenta Bancaria</Label>
                                    <Input type="text" value={selectedUser.n_cuenta_bancaria || '—'} disabled className="form-control" />
                                </FormGroup>
                            </>
                        )}

                        <hr />

                        <FormGroup className="mb-3">
                            <Label for="sueldo_perfil">Remuneración (S/)</Label>
                            <Input
                                type="number"
                                name="sueldo_perfil"
                                id="sueldo_perfil"
                                step="0.01"
                                min="0"
                                value={formData.sueldo_perfil}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="Ingresa la remuneración mensual del colaborador"
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="bono_anual">Bono Anual (S/)</Label>
                            <Input
                                type="number"
                                name="bono_anual"
                                id="bono_anual"
                                step="0.01"
                                min="0"
                                value={formData.bono_anual}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="Ingresa el bono anual si aplica"
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="bono_gestion">Bono de Gestión (S/)</Label>
                            <Input
                                type="number"
                                name="bono_gestion"
                                id="bono_gestion"
                                step="0.01"
                                min="0"
                                value={formData.bono_gestion}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="Ingresa el bono de gestión si aplica"
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="vacaciones_truncas">Liquidación de Vacaciones Truncas (S/)</Label>
                            <Input
                                type="number"
                                name="vacaciones_truncas"
                                id="vacaciones_truncas"
                                step="0.01"
                                min="0"
                                value={formData.vacaciones_truncas}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="Ingresa la liquidación de vacaciones si aplica"
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="descuento">Descuento (S/)</Label>
                            <Input
                                type="number"
                                name="descuento"
                                id="descuento"
                                step="0.01"
                                min="0"
                                value={formData.descuento}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="Ingresa el descuento correspondiente si aplica"
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label for="retencion">Retención 5ta Categoría (S/)</Label>
                            <Input
                                type="number"
                                name="retencion"
                                id="retencion"
                                step="0.01"
                                min="0"
                                value={formData.retencion}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="Ingresa la retención de 5ta categoría si aplica"
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label>% Aporte Trabajador</Label>
                            <Input
                                type="text"
                                value={`${(porcentaje * 100).toFixed(2)}%`}
                                disabled
                                className="form-control"
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label>Total a Pagar Planilla (S/)</Label>
                            <Input
                                type="text"
                                value={new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(totals.total_pagar_planilla)}
                                disabled
                                className="form-control"
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label>Total Aporte Trabajador (S/)</Label>
                            <Input
                                type="text"
                                value={new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(totals.total_aporte)}
                                disabled
                                className="form-control"
                                style={{ color: '#dc2626' }}
                            />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label>Importe Neto a Pagar (S/)</Label>
                            <Input
                                type="text"
                                value={new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(totals.importe_neto)}
                                disabled
                                className="form-control fw-bold"
                            />
                        </FormGroup>

                        <div className="flex justify-end mt-4 pb-3">
                            <Button
                                className="btn-primary px-5 py-2"
                                onClick={handleSave}
                                style={{ borderRadius: '8px' }}
                                disabled={isLoading}
                            >
                                GUARDAR
                            </Button>
                        </div>

                    </Form>
                </ModalBody>
            </div>
        </Modal>
    );
};

export default ModalAgregarAdministracionCargoPago;
